/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { AfterContentInit, Directive, ElementRef, Host } from '@angular/core';
import { Observable, Subject, combineLatest, fromEvent } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { KeyboardListDirective } from './keyboard-list.directive';
import { KeyboardPipeSource } from './keyboard.utils';

// Seit Angular 10 müssen abstrakte Oberklassen auch mit Metadaten annotiert werden
// https://angular.io/guide/migration-undecorated-classes
@Directive()
export abstract class KeyboardListNavigationDirective<T> implements AfterContentInit {

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        @Host() private keyboardList: KeyboardListDirective<T>,
        private elementRef: ElementRef) {
    }

    abstract getNavigationOperator(): (source$: KeyboardPipeSource<T>) => Observable<number>;

    abstract getNavigationKeysFilterPredicate(): (event: KeyboardEvent) => boolean;

    ngAfterContentInit(): void {
        this.getPositionIndex().pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(index => this.keyboardList.moveTo(index));

        this.getKeyDownEvent().pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(e => e.preventDefault());
    }

    private getPositionIndex(): Observable<number> {
        const items$ = this.keyboardList.items.changes.pipe(
            startWith(this.keyboardList.items.toArray())
        );

        return combineLatest([this.getKeyDownEvent(), items$]).pipe(
            map(([event, items]) => ([event, this.keyboardList.getSelectedIndex(), items])),
            this.getNavigationOperator(),
        )
    }

    private getKeyDownEvent(): Observable<KeyboardEvent> {
        const keydownEvent$ = fromEvent<KeyboardEvent>(this.elementRef.nativeElement, 'keydown').pipe(
            filter(this.getNavigationKeysFilterPredicate())
        );

        return combineLatest([this.keyboardList.enabled$, keydownEvent$]).pipe(
            filter(([enabled]) => !!enabled),
            map(([_, event]) => event),
        );
    }
}
